// src/features/developer/components/DeveloperDashboard.tsx
import React, { Suspense, useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import Tabs from '../../../../shared/components/ui/Tabs'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useRbac } from '@/shared/auth'
import { isAuthDisconnected } from '@/lib/auth/authDisconnect'
import SimulatorPanel from './simulations/SimulatorPanel'
import LoggerPanel from './logger/LoggerPanel'
import Testing from './testing/Testing'
import WorkflowRunnerPanel from './workflow/runner/WorkflowRunnerPanel'
import { lazyWithRetry } from '@/utils/lazyWithRetry'
import { AudioAnalyzerPanel } from './audio-analyzer'
import { VideoGalleryPanel } from './video-gallery'
import { VideoEditorPanel } from './video-editor'

const TextToSpeechPanel  = lazyWithRetry(() => import('@/features/services/google-apis/components/TextToSpeechPanel'))
const SpeechToTextPanel = lazyWithRetry(() => import('@/features/services/google-apis/components/SpeechToTextPanel'))
const ImageGenPanel     = lazyWithRetry(() => import('@/features/services/google-apis/components/ImageGenPanel'))
const FlowExplorerPanel = lazyWithRetry(() => import('./workflow/FlowExplorerPanel'))
const TelemetryCenterPage = lazyWithRetry(() => import('@/features/telemetry-center/components/TelemetryCenterPage'))

const lazyFallback = (
  <div className="flex items-center justify-center h-40 text-neutral-600 text-sm">
    Loading…
  </div>
)

// ── Google APIs sub-tabs ──────────────────────────────────────────────────
const GOOGLE_API_TABS = ['Text-to-Speech', 'Speech-to-Text', 'Icon Generator'] as const
type GoogleApiTab = typeof GOOGLE_API_TABS[number]

function GoogleApisSection() {
  // Read initial tab from URL (enables BubbleNav deep-linking on mobile)
  const [sp] = useSearchParams();
  const isValidGApiTab = (v: string | null): v is GoogleApiTab =>
    v === 'Text-to-Speech' || v === 'Speech-to-Text' || v === 'Icon Generator';

  const [apiTab, setApiTab] = useState<GoogleApiTab>(() => {
    const v = sp.get('gApiTab');
    return isValidGApiTab(v) ? v : 'Text-to-Speech';
  })

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('gApiTab');
    if (isValidGApiTab(v) && v !== apiTab) setApiTab(v);
  }, [sp]);

  // Write gApiTab back to URL so reloads restore the active sub-tab
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('gApiTab') !== apiTab) {
        u.searchParams.set('gApiTab', apiTab);
        window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
      }
    } catch {}
  }, [apiTab]);

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {/* Mini pill tabs — desktop only; BubbleNav handles mobile */}
      <div className="hidden md:flex gap-2 shrink-0">
        {GOOGLE_API_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setApiTab(tab)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              apiTab === tab
                ? 'bg-blue-600 text-white'
                : 'pac-glass-btn text-neutral-400 hover:border-neutral-500 hover:text-neutral-200'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <Suspense fallback={lazyFallback}>
          {apiTab === 'Text-to-Speech' && <TextToSpeechPanel />}
          {apiTab === 'Speech-to-Text' && <SpeechToTextPanel />}
          {apiTab === 'Icon Generator' && <ImageGenPanel />}
        </Suspense>
      </div>
    </div>
  )
}

// ── Videos sub-tabs ───────────────────────────────────────────────────────
const VIDEO_TABS = ['Gallery', 'Editor'] as const
type VideoTab = typeof VIDEO_TABS[number]

function VideosSection() {
  // Read initial sub-tab from URL (enables deep-linking).
  const [sp] = useSearchParams();
  const isValidVideoTab = (v: string | null): v is VideoTab =>
    v === 'Gallery' || v === 'Editor';

  const [videoTab, setVideoTab] = useState<VideoTab>(() => {
    const v = sp.get('videosTab');
    return isValidVideoTab(v) ? v : 'Gallery';
  })

  // Sync with URL when navigation happens while already mounted.
  useEffect(() => {
    const v = sp.get('videosTab');
    if (isValidVideoTab(v) && v !== videoTab) setVideoTab(v);
  }, [sp]);

  // Write videosTab back to URL so reloads restore the active sub-tab.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get('videosTab') !== videoTab) {
        u.searchParams.set('videosTab', videoTab);
        window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
      }
    } catch {}
  }, [videoTab]);

  return (
    <div className="flex flex-col gap-3 h-full min-h-0">
      {/* Mini pill tabs — desktop only; BubbleNav handles mobile */}
      <div className="hidden md:flex gap-2 shrink-0">
        {VIDEO_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setVideoTab(tab)}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              videoTab === tab
                ? 'bg-blue-600 text-white'
                : 'pac-glass-btn text-neutral-400 hover:border-neutral-500 hover:text-neutral-200'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0">
        {videoTab === 'Gallery' && <VideoGalleryPanel />}
        {videoTab === 'Editor' && <VideoEditorPanel />}
      </div>
    </div>
  )
}

export default function DeveloperDashboard() {
  const { canAny, isSuperadmin } = useRbac()

  // ── Permission-gated tab list ────────────
  // Each tab maps to required permissions; omit = always visible.
  const TAB_PERMS: Record<string, string[] | null> = {
    'Simulator':      null,                                      // always visible for dev users
    'Logger':         ['system.developer.view'],
    'Testing':        ['system.developer.view'],
    'Workflow':       ['workflow.runs.view', 'workflow.runs.admin', 'workflow.runs.trigger'],
    'Flows':          ['workflow.runs.view'],
    'Audio Analyzer': null,                                      // always visible for dev users
    'Videos':         null,                                      // always visible for dev users
    'Telemetry':      ['system.developer.view'],
    'Google APIs':    ['system.developer.view'],
  }

  const subTabLabels = useMemo(() => {
    return Object.entries(TAB_PERMS)
      .filter(([, need]) => !need || isSuperadmin || canAny(...need))
      .map(([label]) => label)
  }, [canAny, isSuperadmin])

  const [sp] = useSearchParams()
  const [activeSubTab, setActiveSubTab] = useState<string>(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('devTab')
      if (v && subTabLabels.includes(v)) return v
    } catch {}
    return subTabLabels[0] ?? 'Simulator'
  })
  useDocumentTitle('Developer', activeSubTab)

  // Sync with URL when BubbleNav navigates while already mounted
  useEffect(() => {
    const v = sp.get('devTab')
    if (v && subTabLabels.includes(v) && v !== activeSubTab) setActiveSubTab(v)
  }, [sp])

  // If the active tab was removed by perm change, fall back.
  // Skip during auth disconnect so the URL stays intact for inline re-login.
  useEffect(() => {
    if (isAuthDisconnected()) return
    if (!subTabLabels.includes(activeSubTab) && subTabLabels.length)
      setActiveSubTab(subTabLabels[0])
  }, [subTabLabels, activeSubTab])

  // Clean stale sub-tab params when switching main tabs.
  // Each sub-panel owns one URL key; remove the others so the URL stays tidy.
  const SUB_TAB_KEYS: Record<string, string> = {
    'Simulator':   'simTab',
    'Workflow':    'runnerTab',
    'Google APIs': 'gApiTab',
    'Videos':      'videosTab',
  }
  useEffect(() => {
    try {
      const u = new URL(window.location.href)
      const keep = SUB_TAB_KEYS[activeSubTab]
      let changed = false
      for (const [, key] of Object.entries(SUB_TAB_KEYS)) {
        if (key !== keep && u.searchParams.has(key)) {
          u.searchParams.delete(key)
          changed = true
        }
      }
      if (changed) window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`)
    } catch {}
  }, [activeSubTab])

  return (
    <section className="h-full min-h-0 flex flex-col gap-6">
      <div className="shrink-0 hidden md:block">
        <h1 className="text-xl font-semibold text-neutral-100">Developer</h1>
        <Tabs
          tabs={subTabLabels}
          activeTab={activeSubTab}
          onTabChange={setActiveSubTab}
          urlKey="devTab"
        />
      </div>

      <div className="flex-1 min-h-0">
        {activeSubTab === 'Simulator' && <SimulatorPanel />}
        {activeSubTab === 'Logger' && <LoggerPanel />}
        {activeSubTab === 'Testing' && <Testing />}
        {activeSubTab === 'Workflow' && <WorkflowRunnerPanel />}
        {activeSubTab === 'Flows' && <Suspense fallback={lazyFallback}><FlowExplorerPanel /></Suspense>}
        {activeSubTab === 'Audio Analyzer' && <AudioAnalyzerPanel />}
        {activeSubTab === 'Videos' && <VideosSection />}
        {activeSubTab === 'Telemetry' && <Suspense fallback={lazyFallback}><TelemetryCenterPage /></Suspense>}
        {activeSubTab === 'Google APIs' && <GoogleApisSection />}
      </div>
    </section>
  )
}
